import type { WatchQueryFetchPolicy } from "@apollo/client";
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  TableMeta,
  useReactTable,
} from "@tanstack/react-table";
import { Box, Text } from "@chakra-ui/react";

import { DocumentType, getFragmentData, gql } from "__generated__/gql";
import {
  useChakraReactTableProps,
  usePrepApolloData,
} from "components/Shared/ChakraReactTable/utils";
import { useActiveBrandIdContext } from "utils/apollo/hooks/brand";
import usePagination, {
  usePaginationReset,
} from "components/Shared/ChakraReactTable/usePagination";
import useGQLSorting, {
  GQLOrderByEnumsById,
} from "components/Shared/ChakraReactTable/useGQLSorting";
import {
  formatAddressCell,
  formatJustifiedDateTimeCell,
} from "components/Shared/ChakraReactTable/formatters";
import useStickyHeader from "components/Shared/ChakraReactTable/useStickyHeader";
import { NameWithFlagIcons } from "components/Shared/RetailerDirectory/utils";
import {
  RetailerLocationWithIdentifierFilter,
  RetailerLocationWithIdentifiersOrderBy,
} from "__generated__/gql/graphql";
import useFixedLayout from "components/Shared/ChakraReactTable/useFixedLayout";
import useSelectableRow from "components/Shared/ChakraReactTable/useSelectableRow";
import { useCurrentBrandId } from "utils/currentUserHooks";
import IsCustomBadge from "components/Shared/RetailerDirectory/IsCustomBadge";
import { useQuery } from "utils/apollo/hooks";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import FullPageTable, {
  FullPageTableProps,
} from "components/Shared/ChakraReactTable/FullPageTable";
import RetailerTypeBadge from "components/Shared/RetailerDirectory/RetailerTypeBadge";
import useArchivedRowProps from "components/Shared/ChakraReactTable/useArchivedRowProps";
import LocationName from "components/Shared/RetailerDirectory/Locations/LocationName";
import useStaticColumnPinning from "components/Shared/ChakraReactTable/useStaticColumnPinning";
import { ComponentProps } from "components/Shared/ChakraReactTable";

export const fragment = gql(/* GraphQL */ `
  fragment RetailerLocationsDirectoryTableRow on RetailerLocationWithIdentifier {
    id
    displayNameWithRetailer
    retailerLocation {
      id
      name
      storeNumber
      website
      isArchived
      isUnknown
      createdAt
      retailer {
        id
        nameWithUniqueSuffix
        isArchived
        isCustom
        retailerType
        ...RetailerNameWithFlagIconsFragment
        ...RetailerIsCustomBadge
      }
      address {
        id
        ...AddressComponentFields
      }
    }
    ...RetailerLocationNameFragment
  }
`);

const query = gql(/* GraphQL */ `
  query RetailerLocationsDirectoryTable(
    $filter: RetailerLocationWithIdentifierFilter
    $orderBy: [RetailerLocationWithIdentifiersOrderBy!] = [
      RETAILER_LOCATION__CREATED_AT_ASC
    ]
    $limit: Int = 10
    $offset: Int = 0
  ) {
    retailerLocationWithIdentifiers(
      filter: $filter
      first: $limit
      offset: $offset
      orderBy: $orderBy
    ) {
      totalCount
      nodes {
        id
        ...RetailerLocationsDirectoryTableRow
      }
    }
  }
`);

export type Datum = DocumentType<typeof fragment>;

const col = createColumnHelper<Datum>();

export const RETAILER_COLUMNS = [
  col.accessor((r) => r.retailerLocation?.retailer?.nameWithUniqueSuffix, {
    id: "retailerName",
    header: "Retailer",
    cell: (cell) => (
      <NameWithFlagIcons
        retailer={cell.row.original.retailerLocation?.retailer}
      />
    ),
    size: 250,
  }),
  col.accessor((r) => r.retailerLocation?.retailer?.retailerType, {
    id: "retailerType",
    header: "Type",
    cell: (cell) => {
      const retailerType = cell.getValue();
      if (!retailerType) return null;
      return (
        <RetailerTypeBadge
          retailerType={retailerType}
          isTruncated
          maxW="100%"
          fontSize=".8em"
          lineHeight={1.5}
        />
      );
    },
    size: 120,
    meta: { props: { textAlign: "center" }, cellProps: { isTruncated: true } },
  }),
  col.accessor((r) => r.retailerLocation?.retailer?.isCustom, {
    id: "isCustom",
    header: "Custom?",
    cell: (cell) =>
      cell.row.original.retailerLocation?.retailer && (
        <IsCustomBadge
          retailer={cell.row.original.retailerLocation?.retailer}
        />
      ),
    size: 120,
    meta: { props: { textAlign: "center" } },
  }),
];

export const LOCATION_COLUMNS = [
  col.accessor((r) => r.displayNameWithRetailer, {
    id: "name",
    header: "Name",
    cell: (cell) => <LocationName location={cell.row.original} />,
    size: 175,
    meta: { pinned: "left" },
  }),
  col.accessor((r) => r.retailerLocation?.address, {
    id: "address",
    header: "Address",
    cell: formatAddressCell,
    size: 220,
    enableSorting: false,
    meta: { cellProps: { fontSize: ".9em", lineHeight: "normal" } },
  }),
  col.accessor((r) => r.retailerLocation?.storeNumber, {
    id: "storeNumber",
    header: "Store #",
    cell: (cell) => {
      const storeNumber = cell.getValue();
      if (typeof storeNumber !== "number") return null;
      return (
        <>
          <Box as="span" fontWeight={200} opacity={0.8}>
            #
          </Box>
          {storeNumber}
        </>
      );
    },
    size: 110,
    meta: {
      props: { isNumeric: true },
      cellProps: {
        pr: "3em",
        fontFamily: "mono",
        fontWeight: "normal",
        opacity: 0.8,
      },
    },
  }),
  col.accessor((r) => r.retailerLocation?.createdAt, {
    id: "createdAt",
    header: "Added",
    cell: (cell) => formatJustifiedDateTimeCell(cell),
    meta: {
      props: { textAlign: "right" },
      cellProps: { fontSize: ".85em", lineHeight: "normal" },
    },
    size: 115,
  }),
];

export const DEFAULT_COLUMNS = [
  ...LOCATION_COLUMNS.slice(0, -1),
  ...RETAILER_COLUMNS,
  ...LOCATION_COLUMNS.slice(-1),
];

export const orderByEnumsById: GQLOrderByEnumsById<RetailerLocationWithIdentifiersOrderBy> =
  {
    name: [
      RetailerLocationWithIdentifiersOrderBy.DISPLAY_NAME_WITH_RETAILER_ASC,
      RetailerLocationWithIdentifiersOrderBy.DISPLAY_NAME_WITH_RETAILER_DESC,
    ],
    storeNumber: [
      RetailerLocationWithIdentifiersOrderBy.RETAILER_LOCATION__STORE_NUMBER_ASC,
      RetailerLocationWithIdentifiersOrderBy.RETAILER_LOCATION__STORE_NUMBER_DESC,
    ],
    retailerName: [
      RetailerLocationWithIdentifiersOrderBy.RETAILER__NAME_ASC,
      RetailerLocationWithIdentifiersOrderBy.RETAILER__NAME_DESC,
    ],
    retailerType: [
      RetailerLocationWithIdentifiersOrderBy.RETAILER__RETAILER_TYPE_ASC,
      RetailerLocationWithIdentifiersOrderBy.RETAILER__RETAILER_TYPE_DESC,
    ],
    isCustom: [
      RetailerLocationWithIdentifiersOrderBy.RETAILER__IS_CUSTOM_ASC,
      RetailerLocationWithIdentifiersOrderBy.RETAILER__IS_CUSTOM_DESC,
    ],
    createdAt: [
      RetailerLocationWithIdentifiersOrderBy.RETAILER_LOCATION__CREATED_AT_ASC,
      RetailerLocationWithIdentifiersOrderBy.RETAILER_LOCATION__CREATED_AT_DESC,
    ],
  };

export type RetailerLocationsDirectoryTableProps = Pick<
  FullPageTableProps<Datum>,
  "scrollContainerProps" | "tableContainerProps" | "tableControlsFooterProps"
> & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns?: ColumnDef<Datum, any>[];
  defaultPageSize?: number;
  filter?: RetailerLocationWithIdentifierFilter;
  isAdmin?: boolean;
  isReady?: boolean;
  meta?: TableMeta<Datum>;
  nextFetchPolicy?: WatchQueryFetchPolicy;
  onSelectedChange?: (id: UUID) => void;
  selected?: UUID | null;
  tableProps?: Partial<ComponentProps<Datum>>[];
};

export default function RetailerLocationsDirectoryTable({
  columns = DEFAULT_COLUMNS,
  defaultPageSize = 10,
  filter,
  isAdmin,
  isReady = true,
  meta,
  nextFetchPolicy,
  onSelectedChange,
  selected,
  scrollContainerProps,
  tableProps: extraTableProps = [],
  tableContainerProps,
  tableControlsFooterProps,
}: RetailerLocationsDirectoryTableProps) {
  const {
    pagination,
    onPaginationChange,
    limitOffset,
    getPageCount,
    setPageIndex,
  } = usePagination({ pageSize: defaultPageSize });

  const { sorting, onSortingChange, orderBy } = useGQLSorting(
    orderByEnumsById,
    [{ id: "createdAt", desc: false }],
  );

  const brandId = useCurrentBrandId();

  const isPendingFilterReset = usePaginationReset(
    filter,
    setPageIndex,
    pagination.pageIndex,
  );

  const isPendingSortingReset = usePaginationReset(
    sorting,
    setPageIndex,
    pagination.pageIndex,
  );

  const brandContext = useActiveBrandIdContext();

  const {
    data: rawData,
    loading: isLoading,
    refetch,
  } = useQuery(query, {
    context: isAdmin ? ADMIN_CONTEXT : brandContext,
    nextFetchPolicy,
    skip: !isReady || !brandId || isPendingFilterReset || isPendingSortingReset,
    variables: brandId
      ? { brandId, filter, orderBy, ...limitOffset }
      : undefined,
  });

  const { nodes, totalCount } = rawData?.retailerLocationWithIdentifiers || {};

  const columnPinning = useStaticColumnPinning(columns);

  const tableInstance = useReactTable({
    columns,
    meta,
    data: usePrepApolloData(getFragmentData(fragment, nodes)),
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange,
    onSortingChange,
    manualPagination: true,
    manualSorting: true,
    enableSorting: true,
    state: { columnPinning, pagination, sorting },
    pageCount: getPageCount(totalCount),
  });

  const selectableRowProps = useSelectableRow<Datum>({
    selected,
    onSelectedChange: onSelectedChange ?? (() => {}),
  });

  const tableProps = useChakraReactTableProps<Datum>([
    useStickyHeader(),
    useFixedLayout(tableInstance),
    onSelectedChange ? selectableRowProps : {},
    useArchivedRowProps(
      (r) =>
        r.original.retailerLocation?.isArchived ||
        r.original.retailerLocation?.retailer?.isArchived,
    ),
    {
      tableProps: { fontSize: "sm", variant: "condensed" },
      tableBodyProps: { sx: { "> tr > td": { py: 2 } } },
    },
    ...extraTableProps,
  ]);

  return (
    <FullPageTable
      tableInstance={tableInstance}
      {...tableProps}
      {...{
        scrollContainerProps,
        tableContainerProps,
        tableControlsFooterProps,
      }}
      isLoading={isLoading || !isReady || typeof window === "undefined"}
      noRowsPlaceholder={<Text>No locations found.</Text>}
      totalCount={totalCount}
      resultTypeLabel="locations"
      onRefresh={refetch}
    />
  );
}
